import { Component, OnDestroy, OnInit } from '@angular/core'
import { Auth, signOut } from '@angular/fire/auth'
import { selectors } from '@employer/app/store/selectors'
import { NotificationHelper } from '@engineering11/web-ui-helpers'
import { Store } from '@ngrx/store'
import { combineLatest, Subject, timer } from 'rxjs'
import { map, shareReplay, takeUntil } from 'rxjs/operators'
import { userActions } from 'shared-lib'

/**
 * Persistent "acting as" banner for admin impersonation sessions. Reads impersonation state off the
 * decoded sign-in token (`getIsImpersonating`/`getActAsExpiresAt`/`getImpersonationCustomerLabel`),
 * shows a live remaining-time countdown, and offers an Exit control that ends the shadow session and
 * returns to the tenant admin by closing this admin-opened tab. On expiry it self-limits: notifies
 * once, then runs the same exit.
 *
 * Declared in `SharedModule` (same module as `MainLayoutComponent`) and mounted once, above the
 * nav+main row, in `main-layout.component.ts`. `MainLayoutComponent` renders only on authenticated
 * routes, so this banner never shows on `/auth`, `/act-as`, or `/registration`.
 */
@Component({
  selector: 'app-impersonation-banner',
  templateUrl: './impersonation-banner.component.html',
})
export class ImpersonationBannerComponent implements OnInit, OnDestroy {
  private destroy$ = new Subject<void>()
  /** One-shot guard so expiry only notifies + logs out once, even though remaining$ keeps ticking. */
  private expiredHandled = false

  isImpersonating$ = this.store.select(selectors.getIsImpersonating)
  customerLabel$ = this.store.select(selectors.getImpersonationCustomerLabel)

  /**
   * Milliseconds remaining until `actAsExpiresAt`, re-derived every second. `null` when the claim is
   * absent or not a finite number (undocumented unit: treat values `> 1e12` as epoch ms, else epoch
   * seconds), so the template can hide the countdown region without crashing.
   */
  remaining$ = combineLatest([this.store.select(selectors.getActAsExpiresAt), timer(0, 1000)]).pipe(
    map(([expiresAt]) => {
      if (typeof expiresAt !== 'number' || !isFinite(expiresAt)) return null
      const expiresAtMs = expiresAt > 1e12 ? expiresAt : expiresAt * 1000
      return expiresAtMs - Date.now()
    }),
    shareReplay({ bufferSize: 1, refCount: true })
  )

  constructor(private store: Store, private notifications: NotificationHelper, private auth: Auth) {}

  ngOnInit(): void {
    // async pipe drives display; this subscription only exists to fire the one-shot expiry side effect.
    this.remaining$.pipe(takeUntil(this.destroy$)).subscribe(remaining => {
      if (remaining != null && remaining <= 0 && !this.expiredHandled) {
        this.notifications.popNotification({
          type: 'warning',
          title: 'Session expired',
          message: 'Your acting-as session has expired.',
        })
        this.exit()
      }
    })
  }

  ngOnDestroy(): void {
    this.destroy$.next()
    this.destroy$.complete()
  }

  /**
   * Ends the acting-as session. Signs out FIRST — `OnLogOut` clears the stored tokens/cookie and the
   * awaited Firebase sign-out clears auth persistence — so the shadow identity is never resumable from
   * this browser after Exit. Only then close the tab (the admin app opened it via `window.open`, so
   * closing returns focus to the admin's still-authenticated tab). If the tab was not script-opened,
   * close is a no-op and the sign-out's redirect lands on the login screen instead.
   */
  exit(): void {
    if (this.expiredHandled) return
    this.expiredHandled = true
    this.store.dispatch(userActions.OnLogOut())
    signOut(this.auth)
      .catch(() => undefined)
      .then(() => window.close())
  }

  /** Formats milliseconds remaining as `mm:ss`, floored to whole seconds and never negative. */
  formatRemaining(remainingMs: number): string {
    const totalSeconds = Math.max(0, Math.floor(remainingMs / 1000))
    const minutes = Math.floor(totalSeconds / 60)
    const seconds = totalSeconds % 60
    return `${minutes}:${seconds.toString().padStart(2, '0')}`
  }
}
